import { useEffect, useState, useRef, useCallback } from 'react';
import { api } from '../api/client';
import type { BillRun, BillingPocket, CreateBillRunRequest, VerificationBill, PocketTenant } from '../api/types';
import { Badge, Button, Card, Alert } from '../components/ui';
import { billRunStatusTone } from '../lib/billRunStatusTone';
import BillRunsTable from './billRuns/BillRunsTable';
import CreateBillRunDialog from './billRuns/CreateBillRunDialog';
import RerunDialog from './billRuns/RerunDialog';
import DownloadMonthlyDialog from './billRuns/DownloadMonthlyDialog';
import { fmtCurrencyOrDash } from '../lib/fmt';
import { defaultBillRunPeriod } from '../lib/billRunPeriod';

export default function BillRuns() {
  const [billRuns, setBillRuns] = useState<BillRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDownloadMonthly, setShowDownloadMonthly] = useState(false);
  const [creating, setCreating] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Bulk Re-Run selection + modal. Only 'pending_approval' runs are
  // re-runnable (matches the backend guard in POST /api/bill-runs/:id/rerun
  // and the single-bill Re-Run on the detail page). We key the selection by
  // run id -> the BillRun captured at tick time (a Map, not a Set of ids):
  // this keeps the selection self-contained so it survives the server-side
  // status filter, which REPLACES `billRuns` with only the filtered slice.
  // rerunTargets is a further snapshot taken when the modal opens so the 3s
  // background poll can't reshuffle the modal rows mid-flow.
  const [selectedRuns, setSelectedRuns] = useState<Map<string, BillRun>>(new Map());
  const [showRerunModal, setShowRerunModal] = useState(false);
  const [rerunTargets, setRerunTargets] = useState<BillRun[]>([]);
  const [rerunReasons, setRerunReasons] = useState<Record<string, string>>({});
  const [rerunResults, setRerunResults] = useState<Record<string, { ok: boolean; message?: string }>>({});
  const [rerunningIds, setRerunningIds] = useState<Set<string>>(new Set());
  const [rerunningAll, setRerunningAll] = useState(false);
  // Synchronously-tracked in-flight ids. Unlike rerunningIds (state) and the
  // button `disabled` attr — both of which only flip after a React commit —
  // this Set is mutated before the await, closing the sub-frame double-click
  // window that could otherwise fire two concurrent POSTs for the same run.
  const inFlightRef = useRef<Set<string>>(new Set());

  // Form state
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [pockets, setPockets] = useState<BillingPocket[]>([]);
  const [buildings, setBuildings] = useState<Array<{ id: string; name: string }>>([]);
  const [tenantsByPocket, setTenantsByPocket] = useState<Map<string, PocketTenant[]>>(new Map());
  const [selectedPocketIds, setSelectedPocketIds] = useState<Set<string>>(new Set());
  const [pocketSearch, setPocketSearch] = useState('');
  const [loadingPockets, setLoadingPockets] = useState(false);
  // Search categories — toggle which kinds of attributes the search applies to.
  // Selection is always pocket-based; matching by tenant/meter/building just
  // surfaces the parent pocket(s).
  const [searchPockets, setSearchPockets] = useState(true);
  const [searchTenants, setSearchTenants] = useState(true);
  const [searchMeters, setSearchMeters] = useState(true);
  const [searchBuildings, setSearchBuildings] = useState(true);
  // When a tenant pocket is selected and its building's CA pocket has no
  // finalized bill_run covering the requested period, also queue a CA bill
  // run alongside (default ON; user can uncheck to "run anyway").
  const [autoIncludeCa, setAutoIncludeCa] = useState(true);
  const [emailOnFinalize, setEmailOnFinalize] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  // PR 1d — Schedule §6 partial-month flag. Default off (calendar months
  // bill at the full monthly amount); only flipped for true connection /
  // termination bills.
  const [partialMonthProrata, setPartialMonthProrata] = useState(false);

  // Verification state
  const [verificationMode, setVerificationMode] = useState(false);
  const [_verificationFile, setVerificationFile] = useState<File | null>(null);
  const [verificationBill, setVerificationBill] = useState<VerificationBill | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [extractionProgress, setExtractionProgress] = useState('');
  const [reviewStep, setReviewStep] = useState(false);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadBillRuns = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const params: { status?: string } = {};
      if (filterStatus) params.status = filterStatus;
      const res = await api.getBillRuns(params);
      setBillRuns(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load bill runs');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    loadBillRuns();
  }, [loadBillRuns]);

  // Load buildings on page mount so the bill-run list search can match by
  // building name without waiting for the create-bill-run modal to open.
  useEffect(() => {
    api.getBuildings().then(res => setBuildings(res.data)).catch(() => { /* swallow */ });
  }, []);

  // Poll every 3s while any bill run is active
  useEffect(() => {
    const hasActive = billRuns.some(r => r.status === 'pending' || r.status === 'calculating');
    if (hasActive) {
      pollRef.current = setInterval(() => loadBillRuns(true), 3000);
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [billRuns, loadBillRuns]);

  const loadPockets = async () => {
    try {
      setLoadingPockets(true);
      // Fetch pockets + the full tenant history in parallel so the search box
      // can match on past tenant names too. Selection is still pocket-based;
      // tenants surface their pocket when their name matches the query.
      const [pocketsRes, tenantsRes, buildingsRes] = await Promise.all([
        api.getPockets(),
        api.getTenants({ include_history: true }),
        api.getBuildings(),
      ]);
      setPockets(pocketsRes.data);
      setBuildings(buildingsRes.data);
      const map = new Map<string, PocketTenant[]>();
      for (const t of tenantsRes.data) {
        const arr = map.get(t.pocket_id) ?? [];
        arr.push(t);
        map.set(t.pocket_id, arr);
      }
      setTenantsByPocket(map);
    } catch (err) {
      console.error('Failed to load pockets/tenants:', err);
    } finally {
      setLoadingPockets(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      setError('File too large. Maximum 10MB.');
      return;
    }
    setVerificationFile(file);
    setExtracting(true);
    setExtractionProgress('Uploading PDF...');
    setError(null);
    try {
      setExtractionProgress('Extracting bill data...');
      const res = await api.uploadVerificationBill(file);
      setVerificationBill(res.data);
      if (res.data.extraction_status === 'error') {
        setError(`Extraction failed: ${res.data.extraction_error || 'Unknown error'}`);
        setExtracting(false);
        return;
      }
      setExtractionProgress('Extraction complete');
      setReviewStep(true);

      // Auto-select pocket if account number matches
      if (res.data.account_number) {
        const matchingPocket = pockets.find(p => p.account_number === res.data.account_number);
        if (matchingPocket) {
          setSelectedPocketIds(new Set([matchingPocket.id]));
        }
      }

      // Auto-fill dates from extraction
      if (res.data.billing_period_start) setPeriodStart(res.data.billing_period_start);
      if (res.data.billing_period_end) setPeriodEnd(res.data.billing_period_end);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to extract bill data');
    } finally {
      setExtracting(false);
    }
  };

  const handleCreate = async () => {
    if (!periodStart || !periodEnd) {
      setError('Please select both start and end dates');
      return;
    }

    if (selectedPocketIds.size === 0) {
      setError('Please select at least one billing pocket');
      return;
    }

    if (emailOnFinalize && !emailRecipient.trim()) {
      setError('Please enter an email address for notifications');
      return;
    }

    if (verificationMode && !verificationBill) {
      setError('Please upload and review a council bill first');
      return;
    }

    if (verificationMode && verificationBill && verificationBill.extraction_status !== 'confirmed') {
      // Confirm the verification bill values before creating the run
      try {
        await api.confirmVerificationBill(verificationBill.id, {});
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to confirm verification bill');
        return;
      }
    }

    try {
      setCreating(true);
      setError(null);
      // Build the final pocket-id list: explicitly selected pockets, plus
      // any auto-included CA pockets when the user kept the default
      // safety-net checkbox enabled. CA pockets run first so the supply
      // bill exists when the tenant bills are reviewed (the calculator
      // itself reads OpenRemote directly, but humans approve in order).
      const idsInOrder: string[] = [];
      const seen = new Set<string>();
      if (autoIncludeCa) {
        for (const w of caOutOfSync) {
          if (!seen.has(w.caPocket.id)) { idsInOrder.push(w.caPocket.id); seen.add(w.caPocket.id); }
        }
      }
      for (const id of selectedPocketIds) {
        if (!seen.has(id)) { idsInOrder.push(id); seen.add(id); }
      }
      for (const pocketId of idsInOrder) {
        const data: CreateBillRunRequest = {
          period_start: periodStart,
          period_end: periodEnd,
          pocket_id: pocketId,
          email_on_finalize: emailOnFinalize,
          email_recipient: emailOnFinalize ? emailRecipient.trim() : undefined,
          verification_mode: verificationMode,
          verification_bill_id: verificationMode ? verificationBill?.id : undefined,
          partial_month_prorata: partialMonthProrata,
        };
        await api.createBillRunV2(data);
      }
      setShowCreateModal(false);
      setPeriodStart('');
      setPeriodEnd('');
      setSelectedPocketIds(new Set());
      setPocketSearch('');
      setEmailOnFinalize(false);
      setEmailRecipient('');
      setPartialMonthProrata(false);
      setVerificationMode(false);
      setVerificationFile(null);
      setVerificationBill(null);
      setReviewStep(false);
      await loadBillRuns();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create bill run');
    } finally {
      setCreating(false);
    }
  };

  // Single point for every way the Create dialog can close — the explicit
  // Cancel button, and (now that it's on ResponsiveDialog) Escape, the
  // header close button, and a backdrop click. Mirrors what the old
  // hand-rolled Cancel button did, so all four dismiss paths reset the
  // same state.
  const handleCreateOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setShowCreateModal(true);
      return;
    }
    setShowCreateModal(false);
    setError(null);
    setVerificationMode(false);
    setVerificationFile(null);
    setVerificationBill(null);
    setReviewStep(false);
  };

  const handleVerificationModeChange = (checked: boolean) => {
    setVerificationMode(checked);
    if (!checked) {
      setVerificationFile(null);
      setVerificationBill(null);
      setReviewStep(false);
    }
  };

  const handleEmailOnFinalizeChange = (checked: boolean) => {
    setEmailOnFinalize(checked);
    if (!checked) setEmailRecipient('');
  };

  const handleReupload = () => {
    setReviewStep(false);
    setVerificationBill(null);
    setVerificationFile(null);
  };

  // Atomically select/deselect every pocket in a building group in one
  // state update (mirrors the pre-extraction inline handler) rather than
  // toggling pockets one at a time.
  const toggleGroup = (groupIds: string[], allSelected: boolean) => {
    setSelectedPocketIds(prev => {
      const next = new Set(prev);
      if (allSelected) groupIds.forEach(id => next.delete(id));
      else groupIds.forEach(id => next.add(id));
      return next;
    });
  };

  const setDefaultDates = () => {
    const { start, end } = defaultBillRunPeriod(new Date());
    setPeriodStart(start);
    setPeriodEnd(end);
  };

  // Converged onto the shared formatter (src/lib/fmt.ts), which is where the
  // non-breaking separator after "R" lives — the hand-rolled ASCII space was
  // the only break opportunity in the string and orphaned the "R" onto its own
  // line in narrow table columns.
  const formatCurrency = fmtCurrencyOrDash;

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Routed through ui/Badge, now that Badge has 7 tones (the two added —
  // `purple`, `orange` — for exactly this). 7 distinct visual states
  // (pending, calculating, pending_approval, approved, finalized, error,
  // error+Rejected) map 1:1 onto the 7 tones, so no two genuinely different
  // statuses render identically:
  //   pending          -> neutral   (was gray)
  //   calculating      -> info      (was blue)
  //   pending_approval -> warning   (was yellow)
  //   approved         -> success   (was green)
  //   finalized        -> purple    (unchanged hue)
  //   error            -> danger    (was red)
  //   error (Rejected) -> orange    (unchanged hue)
  // Labels are unchanged from the hand-rolled version.
  //
  // The map itself now lives in src/lib/billRunStatusTone.ts. This screen was
  // its reference implementation (the only complete copy), and the values are
  // byte-for-byte what used to be inlined here — but keeping a local literal
  // is what let Dashboard's and BillRunDetail's copies silently drift off
  // `error: 'danger'`. `billRunStatusTone()` also folds in the `?? 'neutral'`
  // fallback that was previously re-spelled per call site; on the progress
  // branch below that is a no-op either way, since `<Badge tone={undefined}>`
  // already defaults to 'neutral'.
  const getStatusBadge = (status: string, run?: BillRun) => {
    if ((status === 'calculating' || status === 'pending') && run && run.meters_total > 0) {
      const pct = Math.round((run.meters_processed / run.meters_total) * 100);
      return (
        <div className="min-w-[120px]">
          <div className="flex items-center gap-2 mb-1">
            <Badge tone={billRunStatusTone(status)}>
              {status === 'pending' ? 'Queued' : 'Calculating'}
            </Badge>
            <span className="text-xs text-content-muted">{run.meters_processed}/{run.meters_total}</span>
          </div>
          <div className="w-full bg-border-token rounded-full h-1.5">
            <div
              className="bg-blue-500 h-1.5 rounded-full transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      );
    }

    if (status === 'error' && run?.error_message) {
      const isRejected = run.error_message.startsWith('Rejected:');
      return (
        <Badge
          tone={isRejected ? 'orange' : 'danger'}
          title={isRejected ? run.error_message.replace('Rejected: ', '') : run.error_message}
        >
          {isRejected ? 'Rejected' : 'Error'}
        </Badge>
      );
    }

    return (
      <Badge tone={billRunStatusTone(status)}>
        {status.replace('_', ' ')}
      </Badge>
    );
  };

  const today = new Date().toISOString().slice(0, 10);
  const isActiveTenant = (t: PocketTenant) =>
    t.start_date <= today && (t.end_date === null || t.end_date >= today);

  // Per-pocket "why did this match" annotation — one of pocket / tenant / meter.
  // Used to render a small "via …" hint on the row when the match came from
  // something other than the pocket name itself.
  type RowMatch = null | { kind: 'past_tenant' | 'meter'; label: string };
  const matchByPocket = new Map<string, RowMatch>();

  const filteredPockets = pockets.filter(p => {
    if (!pocketSearch) {
      matchByPocket.set(p.id, null);
      return true;
    }
    const q = pocketSearch.toLowerCase();

    // Pocket match (name / shop_no)
    if (searchPockets) {
      if (p.name.toLowerCase().includes(q)) { matchByPocket.set(p.id, null); return true; }
      if ((p.shop_no || '').toLowerCase().includes(q)) { matchByPocket.set(p.id, null); return true; }
    }

    // Tenant match (active or historical)
    if (searchTenants) {
      if ((p.active_tenant_name || '').toLowerCase().includes(q)) { matchByPocket.set(p.id, null); return true; }
      if ((p.active_tenant_account_number || '').toLowerCase().includes(q)) { matchByPocket.set(p.id, null); return true; }
      const tenants = tenantsByPocket.get(p.id) ?? [];
      const tenantHit = tenants.find(t =>
        t.trading_name.toLowerCase().includes(q) ||
        (t.account_number || '').toLowerCase().includes(q) ||
        (t.registered_name || '').toLowerCase().includes(q)
      );
      if (tenantHit) {
        if (!isActiveTenant(tenantHit)) {
          matchByPocket.set(p.id, { kind: 'past_tenant', label: `past: ${tenantHit.trading_name}` });
        } else {
          matchByPocket.set(p.id, null);
        }
        return true;
      }
    }

    // Meter match — check embedded meter summaries from /v2/pockets
    if (searchMeters) {
      const meters = p.meter_summaries ?? [];
      const meterHit = meters.find(m =>
        m.meter_name.toLowerCase().includes(q) ||
        m.meter_asset_id.toLowerCase().includes(q)
      );
      if (meterHit) {
        matchByPocket.set(p.id, { kind: 'meter', label: `meter: ${meterHit.meter_name}` });
        return true;
      }
    }

    // Building match — surfaces every pocket in a building when its name
    // (or its asset id) is searched, so the user can select "all of Dorp
    // Plaza" with one query + Select All in Building.
    if (searchBuildings && p.building_asset_id) {
      const bldg = buildings.find(b => b.id === p.building_asset_id);
      if (bldg && bldg.name.toLowerCase().includes(q)) {
        matchByPocket.set(p.id, null);
        return true;
      }
      if (p.building_asset_id.toLowerCase().includes(q)) {
        matchByPocket.set(p.id, null);
        return true;
      }
    }

    return false;
  });

  // Group filtered pockets by building so the modal renders one section per
  // building with a "Select all in <building>" affordance. Auto-injected CA
  // pockets get bubbled to the top of each group.
  const buildingNameById = new Map(buildings.map(b => [b.id, b.name]));
  const grouped = new Map<string, { name: string; rows: typeof filteredPockets }>();
  for (const p of filteredPockets) {
    const key = p.building_asset_id || '__nobuilding__';
    const name = buildingNameById.get(p.building_asset_id) || 'Unassigned';
    if (!grouped.has(key)) grouped.set(key, { name, rows: [] });
    grouped.get(key)!.rows.push(p);
  }
  // Sort each group: CA pockets first, then by name.
  for (const g of grouped.values()) {
    g.rows.sort((a, b) => {
      if ((a.is_ca ? 1 : 0) !== (b.is_ca ? 1 : 0)) return a.is_ca ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }
  const groupedArr = Array.from(grouped.entries()).sort(([, a], [, b]) => a.name.localeCompare(b.name));

  // For CA out-of-sync detection: compute, given the current period, which
  // CA pockets should be flagged. A CA pocket is "in sync" if there's a
  // finalized bill run on it whose period covers the requested window.
  const periodOk = !!(periodStart && periodEnd);
  const requestedBuildingsWithTenant = new Set<string>();
  for (const id of selectedPocketIds) {
    const p = pockets.find(x => x.id === id);
    if (p && !p.is_ca && p.building_asset_id) requestedBuildingsWithTenant.add(p.building_asset_id);
  }
  const caPocketByBuilding = new Map<string, BillingPocket>();
  for (const p of pockets) {
    if (p.is_ca && p.building_asset_id) caPocketByBuilding.set(p.building_asset_id, p);
  }
  const caOutOfSync: Array<{ buildingName: string; caPocket: BillingPocket; latestPeriod: { start: string; end: string } | null }> = [];
  if (periodOk) {
    for (const buildingId of requestedBuildingsWithTenant) {
      const ca = caPocketByBuilding.get(buildingId);
      if (!ca) continue;
      // Skip if user already explicitly selected the CA pocket too.
      if (selectedPocketIds.has(ca.id)) continue;
      const caRuns = billRuns
        .filter(r => r.pocket_id === ca.id && (r.status === 'finalized' || r.status === 'approved'))
        .sort((a, b) => b.period_end.localeCompare(a.period_end));
      const latest = caRuns[0] ?? null;
      const covers = latest && latest.period_start <= periodStart && latest.period_end >= periodEnd;
      if (!covers) {
        caOutOfSync.push({
          buildingName: buildingNameById.get(buildingId) || buildingId,
          caPocket: ca,
          latestPeriod: latest ? { start: latest.period_start, end: latest.period_end } : null,
        });
      }
    }
  }

  const togglePocket = (id: string) => {
    setSelectedPocketIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllPockets = () => {
    if (selectedPocketIds.size === filteredPockets.length) {
      setSelectedPocketIds(new Set());
    } else {
      setSelectedPocketIds(new Set(filteredPockets.map(p => p.id)));
    }
  };

  // Filter + paginate bill runs. Searches across period dates, run id,
  // pocket name, active tenant name, and building name (resolved via the
  // already-loaded buildings list).
  const buildingNameFor = (id: string | null): string => {
    if (!id) return '';
    const b = buildings.find(x => x.id === id);
    return b?.name || '';
  };
  const filteredBillRuns = billRuns.filter(r => {
    if (search) {
      const q = search.toLowerCase();
      const haystacks = [
        r.period_start,
        r.period_end,
        r.id,
        r.pocket_name || '',
        r.active_tenant_name || '',
        buildingNameFor(r.building_asset_id),
      ].map(s => s.toLowerCase());
      if (!haystacks.some(h => h.includes(q))) return false;
    }
    return true;
  });
  const totalPages = Math.max(1, Math.ceil(filteredBillRuns.length / pageSize));
  const startIdx = (currentPage - 1) * pageSize;
  const paginatedBillRuns = filteredBillRuns.slice(startIdx, startIdx + pageSize);

  const totalSelectedMeters = pockets
    .filter(p => selectedPocketIds.has(p.id))
    .reduce((sum, p) => sum + (p.meter_count ?? 0), 0);

  // ── Bulk Re-Run ────────────────────────────────────────────────────
  // Mirrors the Pre-check flags "Acknowledge All (n missing)" pattern:
  // one justification box per selected run, an individual Re-Run per row,
  // and a gated "Re-Run All" once every box is filled. There is no bulk
  // re-run endpoint, so we fire N serial single-id POSTs — each carries its
  // own justification + audit trail, and partial failure is surfaced per row
  // (a failed row keeps its box so it can be retried).
  const eligiblePageRuns = paginatedBillRuns.filter(r => r.status === 'pending_approval');
  const allEligibleSelected = eligiblePageRuns.length > 0 && eligiblePageRuns.every(r => selectedRuns.has(r.id));
  const someEligibleSelected = eligiblePageRuns.some(r => selectedRuns.has(r.id));
  // How many of the currently-selected runs are visible on this page — used
  // to surface the "select-all is per-page" scope when the selection spans
  // pages the header checkbox can't see.
  const selectedOnPage = paginatedBillRuns.filter(r => selectedRuns.has(r.id)).length;

  const toggleRun = (run: BillRun) => {
    setSelectedRuns(prev => {
      const next = new Map(prev);
      if (next.has(run.id)) next.delete(run.id); else next.set(run.id, run);
      return next;
    });
  };
  const toggleSelectAllOnPage = () => {
    setSelectedRuns(prev => {
      const next = new Map(prev);
      if (allEligibleSelected) eligiblePageRuns.forEach(r => next.delete(r.id));
      else eligiblePageRuns.forEach(r => next.set(r.id, r));
      return next;
    });
  };

  const openRerunModal = () => {
    // Build the target snapshot from the selection Map so the 3s background
    // poll can't reshuffle the modal mid-flow. Prefer the freshest copy from
    // the currently-loaded billRuns (to respect a status change the poll has
    // already seen), else fall back to the object captured at selection time
    // — otherwise a selected run merely filtered out of the current view
    // (server-side status filter) would be wrongly dropped.
    const targets: BillRun[] = [];
    for (const [id, snap] of selectedRuns) {
      const run = billRuns.find(r => r.id === id) || snap;
      if (run.status === 'pending_approval') targets.push(run);
    }
    setRerunTargets(targets);
    setRerunReasons({});
    setRerunResults({});
    setRerunningIds(new Set());
    setShowRerunModal(true);
  };

  const handleReasonChange = (runId: string, value: string) => {
    setRerunReasons(prev => ({ ...prev, [runId]: value }));
  };

  const rerunOne = async (runId: string) => {
    const reason = (rerunReasons[runId] || '').trim();
    // inFlightRef guard is synchronous, so a sub-frame double-click on the
    // per-row button can't fire two concurrent POSTs for the same run.
    if (!reason || rerunResults[runId]?.ok || inFlightRef.current.has(runId)) return;
    inFlightRef.current.add(runId);
    setRerunningIds(prev => new Set(prev).add(runId));
    try {
      await api.rerunBillRun(runId, reason);
      setRerunResults(prev => ({ ...prev, [runId]: { ok: true } }));
    } catch (err) {
      setRerunResults(prev => ({ ...prev, [runId]: { ok: false, message: err instanceof Error ? err.message : 'Re-run failed' } }));
    } finally {
      inFlightRef.current.delete(runId);
      setRerunningIds(prev => { const n = new Set(prev); n.delete(runId); return n; });
    }
  };

  const rerunAll = async () => {
    // Outstanding = not yet successfully re-queued (includes prior failures,
    // so this also retries them). Gate on every outstanding box being filled.
    const outstanding = rerunTargets.filter(r => !rerunResults[r.id]?.ok);
    if (outstanding.length === 0 || outstanding.some(r => !(rerunReasons[r.id] || '').trim())) return;
    setRerunningAll(true);
    for (const run of outstanding) {
      if (inFlightRef.current.has(run.id)) continue;
      const reason = (rerunReasons[run.id] || '').trim();
      inFlightRef.current.add(run.id);
      setRerunningIds(prev => new Set(prev).add(run.id));
      try {
        await api.rerunBillRun(run.id, reason);
        setRerunResults(prev => ({ ...prev, [run.id]: { ok: true } }));
      } catch (err) {
        setRerunResults(prev => ({ ...prev, [run.id]: { ok: false, message: err instanceof Error ? err.message : 'Re-run failed' } }));
      } finally {
        inFlightRef.current.delete(run.id);
        setRerunningIds(prev => { const n = new Set(prev); n.delete(run.id); return n; });
      }
    }
    setRerunningAll(false);
  };

  const closeRerunModal = async () => {
    const succeeded = Object.entries(rerunResults).filter(([, v]) => v.ok).map(([id]) => id);
    setShowRerunModal(false);
    setRerunTargets([]);
    setRerunReasons({});
    setRerunResults({});
    setRerunningIds(new Set());
    if (succeeded.length > 0) {
      // Drop re-queued runs from the selection and refresh so their new
      // pending/calculating status shows immediately.
      setSelectedRuns(prev => { const n = new Map(prev); succeeded.forEach(id => n.delete(id)); return n; });
      await loadBillRuns();
    }
  };

  const outstandingRerunTargets = rerunTargets.filter(r => !rerunResults[r.id]?.ok);
  const rerunMissingCount = outstandingRerunTargets.filter(r => !(rerunReasons[r.id] || '').trim()).length;
  const canRerunAll = outstandingRerunTargets.length > 1 && rerunMissingCount === 0 && !rerunningAll && rerunningIds.size === 0;

  return (
    /* `p-4 lg:p-8`, not a flat `p-8`. Desktop is byte-identical — the change
       only bites below `lg`, and it is the single biggest term in this page's
       375px width budget.

       The earlier mobile passes on this branch all sized against "~343px
       usable at 375px" (see the wave-2 table reports), i.e. they assumed 16px
       page gutters. Every page in this app actually wraps its content in a
       flat `p-8` — 32px a side — so the real budget was 375 - 64 - 2 (the
       Card's own border) = **309px**, 34px less than every calculation that
       drove the primary/secondary column splits. That gap is most of why
       BillRunsTable still scrolled after `rowActions` left the row: the
       column set was correct against a budget the page never actually
       provided.

       32px gutters are 17% of a 375px viewport spent on nothing; 16px is the
       normal mobile value and restores the 343px the rest of this branch was
       designed against. Deliberately NOT applied app-wide here — the other
       pages belong to concurrent work — so /bill-runs and /bill-runs/:id lead
       and the rest should follow in one sweep. AppShell's <main> is
       `min-w-0 overflow-auto`, so nothing about this leaks into the shell. */
    <div className="p-4 lg:p-8">
      {/* Header — wraps so the title and the New Bill Run CTA stack rather
          than the CTA compressing against the right edge at 375px. */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <h1 className="text-2xl font-bold text-content">Bill Runs</h1>
        <div className="flex flex-wrap items-center gap-2">
        <Button variant="secondary" onClick={() => setShowDownloadMonthly(true)}>
          Download Monthly
        </Button>
        <button
          onClick={() => {
            setDefaultDates();
            setSelectedPocketIds(new Set());
            setPocketSearch('');
            loadPockets();
            setShowCreateModal(true);
          }}
          // I6: last un-migrated primary CTA on this page — the other 46
          // primary-action sites already use the accent token (see e.g.
          // Dashboard.tsx:151, Pockets.tsx:118, Button.tsx's `primary`
          // variant). bg-accent/text-accent-fg is registered AA in
          // src/test/contrast.test.ts (accentFg on accent).
          className="tap-target gap-2 px-4 py-2 bg-accent text-accent-fg rounded-lg hover:opacity-90 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Bill Run
        </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <input
          type="text"
          placeholder="Search by date, pocket, tenant, building, or run id…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
          className="min-h-tap px-3 py-2 border border-border-token rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-64"
        />
        <select
          value={filterStatus}
          onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
          className="min-h-tap px-3 py-2 border border-border-token rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="calculating">Calculating</option>
          <option value="pending_approval">Pending Approval</option>
          <option value="approved">Approved</option>
          <option value="finalized">Finalized</option>
        </select>
        <select
          value={pageSize}
          onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
          className="min-h-tap px-3 py-2 border border-border-token rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value={10}>10 per page</option>
          <option value={25}>25 per page</option>
          <option value={50}>50 per page</option>
        </select>
        <span className="text-sm text-content-muted ml-auto">
          {filteredBillRuns.length} run{filteredBillRuns.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Error */}
      {error && !showCreateModal && (
        <Alert tone="danger" className="mb-6">
          {error}
        </Alert>
      )}

      {/* Bulk re-run action bar — appears once one or more re-runnable
          (pending_approval) runs are ticked. Selection persists across
          pages, so the count can exceed what's visible on the page. */}
      {selectedRuns.size > 0 && (
        <Alert tone="warning" className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <span className="text-sm font-medium">
            {selectedRuns.size} bill run{selectedRuns.size !== 1 ? 's' : ''} selected
            {selectedOnPage < selectedRuns.size && (
              <span className="font-normal opacity-80"> · {selectedOnPage} on this page (select-all is per-page)</span>
            )}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedRuns(new Map())}
              className="tap-target px-3 py-1.5 text-sm hover:opacity-70"
            >
              Clear
            </button>
            <Button variant="warning" onClick={openRerunModal}>
              Re-Run Selected ({selectedRuns.size})
            </Button>
          </div>
        </Alert>
      )}

      {/* Table — Card, not a hand-rolled bg-white/border-gray-200 shell: this
          is what Card exists for, and using it here would have prevented
          the reference screen from shipping unreadable in dark mode
          (DataTable's cells are text-content, which is #fafaf9 in dark —
          1.04:1 on literal white). */}
      <Card className="shadow-sm">
        {loading ? (
          <div className="p-8">
            <div className="animate-pulse space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-12 bg-border-token rounded"></div>
              ))}
            </div>
          </div>
        ) : (
          <BillRunsTable
            runs={paginatedBillRuns}
            selectedRuns={selectedRuns}
            eligibleCount={eligiblePageRuns.length}
            allEligibleSelected={allEligibleSelected}
            someEligibleSelected={someEligibleSelected}
            onToggleRun={toggleRun}
            onToggleAll={toggleSelectAllOnPage}
            formatDate={formatDate}
            formatCurrency={formatCurrency}
            getStatusBadge={getStatusBadge}
          />
        )}
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        /* flex-wrap on both rows: five tap-floored pager controls plus the
           "Showing x–y of z" line are wider than 375px minus the page's p-8
           gutters, so without wrapping the pager pushes the page sideways
           instead of stacking under the count. */
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
          <p className="text-sm text-content-muted">Showing {startIdx + 1}–{Math.min(startIdx + pageSize, filteredBillRuns.length)} of {filteredBillRuns.length}</p>
          <div className="flex flex-wrap items-center gap-1">
            <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="tap-target px-3 py-1.5 text-sm border border-border-token rounded-md hover:bg-surface-sunken disabled:opacity-40 disabled:cursor-not-allowed">First</button>
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="tap-target px-3 py-1.5 text-sm border border-border-token rounded-md hover:bg-surface-sunken disabled:opacity-40 disabled:cursor-not-allowed">Prev</button>
            <span className="px-3 py-1.5 text-sm text-content">Page {currentPage} of {totalPages}</span>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="tap-target px-3 py-1.5 text-sm border border-border-token rounded-md hover:bg-surface-sunken disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
            <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="tap-target px-3 py-1.5 text-sm border border-border-token rounded-md hover:bg-surface-sunken disabled:opacity-40 disabled:cursor-not-allowed">Last</button>
          </div>
        </div>
      )}

      <CreateBillRunDialog
        open={showCreateModal}
        onOpenChange={handleCreateOpenChange}
        creating={creating}
        error={error}
        onSubmit={handleCreate}
        verificationMode={verificationMode}
        onVerificationModeChange={handleVerificationModeChange}
        pockets={pockets}
        loadingPockets={loadingPockets}
        pocketSearch={pocketSearch}
        onPocketSearchChange={setPocketSearch}
        searchPockets={searchPockets}
        searchTenants={searchTenants}
        searchMeters={searchMeters}
        searchBuildings={searchBuildings}
        setSearchPockets={setSearchPockets}
        setSearchTenants={setSearchTenants}
        setSearchMeters={setSearchMeters}
        setSearchBuildings={setSearchBuildings}
        filteredPockets={filteredPockets}
        groupedArr={groupedArr}
        matchByPocket={matchByPocket}
        selectedPocketIds={selectedPocketIds}
        onTogglePocket={togglePocket}
        onToggleAllPockets={toggleAllPockets}
        onToggleGroup={toggleGroup}
        totalSelectedMeters={totalSelectedMeters}
        caOutOfSync={caOutOfSync}
        autoIncludeCa={autoIncludeCa}
        onAutoIncludeCaChange={setAutoIncludeCa}
        periodStart={periodStart}
        onPeriodStartChange={setPeriodStart}
        periodEnd={periodEnd}
        onPeriodEndChange={setPeriodEnd}
        partialMonthProrata={partialMonthProrata}
        onPartialMonthProrataChange={setPartialMonthProrata}
        emailOnFinalize={emailOnFinalize}
        onEmailOnFinalizeChange={handleEmailOnFinalizeChange}
        emailRecipient={emailRecipient}
        onEmailRecipientChange={setEmailRecipient}
        extracting={extracting}
        extractionProgress={extractionProgress}
        reviewStep={reviewStep}
        verificationBill={verificationBill}
        onReupload={handleReupload}
        fileInputRef={fileInputRef}
        onFileSelected={handleFileUpload}
      />

      <DownloadMonthlyDialog open={showDownloadMonthly} onOpenChange={setShowDownloadMonthly} />
      <RerunDialog
        open={showRerunModal}
        onRequestClose={closeRerunModal}
        rerunTargets={rerunTargets}
        outstandingRerunTargets={outstandingRerunTargets}
        rerunResults={rerunResults}
        rerunningIds={rerunningIds}
        rerunningAll={rerunningAll}
        rerunReasons={rerunReasons}
        onReasonChange={handleReasonChange}
        onRerunOne={rerunOne}
        onRerunAll={rerunAll}
        rerunMissingCount={rerunMissingCount}
        canRerunAll={canRerunAll}
        formatDate={formatDate}
      />
    </div>
  );
}
